<template>
  <div class="app flex-row align-items-center">
    <DebugWindow v-if="showDebug"></DebugWindow>
    <div class="logo-container">
      <div class="background-overlay"></div>
      <img :src="iris" style="width: 75%; z-index: 2">
    </div>
    <div class="login-container">
      <div class="login-wrapper">
        <span class="welcome-text">WELCOME TO IRIS</span>
        <div class="form-wrapper">
          <div class="input-wrapper">
            <input
                v-model="data.body.email"
                placeholder="Email address"
                class="form-control form-input"
                type="text"
                @input="error = ''"
            >
            <input
                v-model="data.body.password"
                placeholder="Password"
                class="form-control form-input"
                type="password"
                @keypress.enter="login"
                @input="error = ''"
            >
          </div>
          <div class="form-actions">
            <Checkbox :value="data.rememberMe" style="display: none">Remember me</Checkbox>
            <router-link to="login/reset/" class="link">
              Forgot password?
            </router-link>
          </div>
          <button @click="login" class="btn px-4 btn-primary">
            Login
          </button>
          <div class="error-box">
            {{error}}
          </div>
        </div>
      </div>
      <div class="symrise-logo">
        <a href="https://www.symrise.com/" target="_blank">
          <img :src="logo"/>
        </a>
      </div>
    </div>
  </div>
</template>
<script>
import Bowser from 'bowser'
import { mapActions } from 'vuex'

import compatibleBrowsers from '../constants/compatibleBrowsers'
import DebugWindow from '../components/shared/DebugWindow'
import userActions from '../store/modules/users/actionTypes'
import generalActions from '../store/modules/general/actionTypes'
import Checkbox from '../components/shared/Checkbox'
import logo from '../assets/images/logos/symrise-logo.svg'
import iris from '../assets/images/logos/Iris_Logo_Light_Grey_Shadow.svg'
import apiFunctions from '../helpers/apiFunctions'

export default {
  components: { Checkbox, DebugWindow },
  data () {
    return {
      context: 'login context',
      data: {
        body: {
          email: '',
          password: ''
        },
        rememberMe: true,
        fetchUser: true
      },
      error: null,
      errorTimeout: null,
      showDebug: false,
      showErrorToggle: false,
      logo,
      iris
    }
  },
  methods: {
    ...mapActions({
      sendPasswordReset: userActions.SEND_PASSWORD_RESET,
      setError: userActions.SET_LOGIN_ERROR,
      setAlert: generalActions.SET_GLOBAL_ALERT
    }),
    async sendResetEmail (email) {
      let clientAddress = window.location.href.split(this.$route.path)[0]
      clientAddress = clientAddress.replace('http://', '')
      const response = await this.sendPasswordReset({
        email: email,
        client_address: clientAddress
      })
      if (response.status === 200) {
        this.error = 'You have not logged in for over 30 days, for security reasons we\'ve send you an e-mail with a password reset link.'
      } else {
        this.error = response.data
      }
    },
    login () {
      this.errorTimeout = setTimeout(() => {
        this.error = 'Whoops, it seems the server is taking too long to respond...'
      }, 5000)
      this.$auth.login({
        body: this.data.body, // Vue-resource
        data: {
          email: this.data.body.email,
          password: this.data.body.password
        }, // Axios
        staySignedIn: this.data.rememberMe,
        fetchUser: this.data.fetchUser,
        redirect: null
      }).then(async res => {
        clearTimeout(this.errorTimeout)
        if (res.data.password_reset_flag) {
          await this.$auth.logout({ redirect: null })
          await this.sendResetEmail(res.data.email)
        } else {
          this.$auth.user(apiFunctions.createPermissions(res.data))
          this.$router.push({ name: 'home' })

          const browser = Bowser.getParser(window.navigator.userAgent)
          const isValidBrowser = browser.satisfies(compatibleBrowsers)
          if (!isValidBrowser) {
            this.setAlert({ message: 'Your browser is not supported!', type: 'warning' })
            setTimeout(() => this.setAlert({ message: '', type: '' }), 3000)
          }
        }
      }, (res, e) => {
        clearTimeout(this.errorTimeout)
        // TODO re-add login error box
        this.showErrorToggle = true
        this.setError(res)
        window.ress = res
        const status = res.response && res.response.status
        switch (status) {
          case 401:
            this.error = 'Error signing in: provided username or password is incorrect.'
            break
          case 500:
            this.error = 'Something went wrong, please try again later.'
            break
          default:
            this.error = 'Something went wrong, please try again later.'
            break
        }
      })
    }
  }
}
</script>
<style lang="scss" scoped>
@import "../assets/scss/variables";

.logo-container {
  position: relative;
  height: 100vh;
  width: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url("../assets/images/backgrounds/login.jpg");
  background-position: center;
  background-size: cover;
  overflow: hidden;

  .background-overlay {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    height: 100%;
    background-color: #2A274B;
    opacity: 0.4;
  }

  h1 {
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 900;
    font-size: 250px;
    color: $white;
  }
}

.login-container {
  height: 100vh;
  width: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $white;

  .symrise-logo {
    position: fixed;
    bottom: 47px;
    right: 43.5px;
  }

  .login-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    width: 568px;
    height: 537px;
    z-index: 2;

    .welcome-text {
      font-family: 'Source Sans Pro', sans-serif;
      font-weight: 900;
      font-size: 72px;
      line-height: 103px;
      color: $soot;
      letter-spacing: 0;
      white-space: nowrap;
    }

    .form-wrapper {
      width: 400px;

      .input-wrapper {
        height: 115px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .form-input {
          border: none;
          border-bottom: 2px solid $pewter;
        }
      }

      .form-actions {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 60px;
        margin-top: 30px;

        .link {
          font-family: 'Source Sans Pro', sans-serif;
          font-weight: 400;
          font-size: 14px;
          line-height: 25px;
          transition: color 0.1s linear;
          color: $grey-800;
          text-decoration: none;

          &:hover {
            color: $grey-700;
          }
        }
      }

      button {
        background: $blueberry 0 0 no-repeat padding-box;
        border: 1px solid #17469E;
        border-radius: 6px;
        width: 100%;
        height: 50px;
        font-family: 'Source Sans Pro', sans-serif;
        font-weight: 900;
        font-size: 20px;
        line-height: 26px;
        color: $white;
        letter-spacing: 0;

        &:hover {
          background-color: #9256D9;
        }
      }

      .error-box {
        margin-top: 10px;
        height: 40px;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        color: $red-600;
        font-family: 'Source Sans Pro', sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        text-align: center;
      }
    }
  }
}
</style>
